import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import * as api from '../services/api';
import styles from './GalleryPage.module.css';

export default function GalleryPage() {
    const { t } = useLanguage();
    const { isLoggedIn } = useAuth();
    const [activeTab, setActiveTab] = useState('all');
    const [sortBy, setSortBy] = useState('hot');
    const [configurations, setConfigurations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);

    useEffect(() => {
        fetchConfigurations();
    }, [activeTab, sortBy, page, isLoggedIn]);

    async function fetchConfigurations() {
        setLoading(true);
        try {
            const result = await api.getConfigurations({
                tab: activeTab,
                sort: sortBy,
                page,
                limit: 12,
            });
            if (result.success) {
                setConfigurations(result.data || []);
                setTotal(result.total || 0);
            }
        } catch (err) {
            console.error('Failed to fetch configurations:', err);
        } finally {
            setLoading(false);
        }
    }

    // 构建标签页 - 登录后显示"我的配置"
    const tabs = [
        { id: 'all', label: t('gallery.tabs.featured') || 'Featured' },
        { id: 'official', label: t('gallery.tabs.official') || 'Official' },
        { id: 'community', label: t('gallery.tabs.community') || 'Community' },
        // 登录后才显示"我的配置"
        ...(isLoggedIn ? [{ id: 'mine', label: t('gallery.tabs.mine') || 'My Configs' }] : []),
    ];

    // 获取状态徽章
    const getStatusBadge = (status) => {
        const statusConfig = {
            draft: { label: t('profile.statusDraft') || 'Draft', class: styles.draft },
            pending: { label: t('profile.statusPending') || 'Pending', class: styles.pending },
            approved: { label: t('profile.statusApproved') || 'Approved', class: styles.approved },
            rejected: { label: t('profile.statusRejected') || 'Rejected', class: styles.rejected },
        };
        const config = statusConfig[status] || statusConfig.draft;
        return <span className={`${styles.statusBadge} ${config.class}`}>{config.label}</span>;
    };

    const sorts = [
        { id: 'hot', label: t('gallery.sort.hot') || 'Hot' },
        { id: 'latest', label: t('gallery.sort.latest') || 'Latest' },
    ];

    return (
        <div className={styles.gallery}>
            <div className={styles.header}>
                <h1 className={styles.title}>{t('gallery.title') || 'Configuration Gallery'}</h1>
                {isLoggedIn && (
                    <Link to="/gallery/upload" className={styles.uploadBtn}>
                        + {t('gallery.upload') || 'Upload'}
                    </Link>
                )}
            </div>

            <div className={styles.filters}>
                <div className={styles.tabs}>
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            className={`${styles.tab} ${activeTab === tab.id ? styles.active : ''}`}
                            onClick={() => { setActiveTab(tab.id); setPage(1); }}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
                <div className={styles.sorts}>
                    {sorts.map(sort => (
                        <button
                            key={sort.id}
                            className={`${styles.sortBtn} ${sortBy === sort.id ? styles.active : ''}`}
                            onClick={() => { setSortBy(sort.id); setPage(1); }}
                        >
                            {sort.label}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className={styles.loading}>
                    <div className={styles.spinner}></div>
                    <p>{t('common.loading') || 'Loading...'}</p>
                </div>
            ) : configurations.length === 0 ? (
                <div className={styles.empty}>
                    <p>{t('gallery.empty') || 'No configurations found'}</p>
                    {isLoggedIn && (
                        <Link to="/gallery/upload" className={styles.uploadBtn}>
                            {t('gallery.beFirst') || 'Be the first to upload!'}
                        </Link>
                    )}
                </div>
            ) : (
                <>
                    <div className={styles.grid}>
                        {configurations.map(config => (
                            <Link
                                key={config.id}
                                to={`/gallery/${config.id}`}
                                className={styles.card}
                            >
                                <div className={styles.cardImage}>
                                    {config.cover_image_url ? (
                                        <img src={config.cover_image_url} alt={config.title} />
                                    ) : (
                                        <div className={styles.placeholder}>{t('common.noImage')}</div>
                                    )}
                                    {config.is_official && (
                                        <span className={styles.officialBadge}>{t('common.official')}</span>
                                    )}
                                    {/* 在"我的配置"标签页显示状态徽章 */}
                                    {activeTab === 'mine' && getStatusBadge(config.status)}
                                </div>
                                <div className={styles.cardContent}>
                                    <h3 className={styles.cardTitle}>{config.title}</h3>
                                    <div className={styles.cardMeta}>
                                        <span className={styles.author}>
                                            {config.user?.nickname || t('common.anonymous')}
                                        </span>
                                        <span className={styles.price}>
                                            ¥{(config.total_price || 0).toLocaleString()}
                                        </span>
                                    </div>
                                    <div className={styles.cardStats}>
                                        <span>❤️ {config.likes_count || 0}</span>
                                        <span>⭐ {config.favorites_count || 0}</span>
                                        <span>💬 {config.comments_count || 0}</span>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>

                    {total > 12 && (
                        <div className={styles.pagination}>
                            <button
                                disabled={page === 1}
                                onClick={() => setPage(p => p - 1)}
                            >
                                Previous
                            </button>
                            <span>Page {page} of {Math.ceil(total / 12)}</span>
                            <button
                                disabled={page >= Math.ceil(total / 12)}
                                onClick={() => setPage(p => p + 1)}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
